import { Card, Form } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useEffect, useRef, useState } from "react";
import WebService from "../../utility/WebService";
import { PencilFill, TrashFill } from "react-bootstrap-icons";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import CustomTableSortable from "../../components/CustomTable/CustomTableSortable";
import HelperService from "../../utility/HelperService";
import AddAdminUser from "./AddAdminUser";
import { toast } from "react-toastify";

export interface AdminUserInterface {
  _id: number;
  fullName: string;
  email: string;
  username: string;
  password: string;
  user_type: "ADMIN" | "USER";
  organization?: string;
  otp?: string | null;
  phone?: string | null;
  temp_otp?: string | null;
  image?: string | null;
  type?: string | null;
  isVerified: string; // 'Y' or 'N'
  verificationToken?: string;
  social_type?: string | null;
  socialid?: string | null;
  status: AdminUserStatusEnum;
  confirmPassword?: string;
  role?: {
    _id: string;
    roleName: string;
  }
}

export enum AdminUserStatusEnum {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  DELETED = "DELETED",
}

const columns = [
  {
    id: "fullName",
    label: "Name",
    minWidth: 170,
    sortable: true,
  },
  {
    id: "username",
    label: "User Name",
    minWidth: 170,
    sortable: false,
  },
  {
    id: "email",
    label: "Email",
    minWidth: 170,
    sortable: false,
  },
  {
    id: "role",
    label: "Role",
    minWidth: 170,
    sortable: false,
  },
  // {
  //     id: "phone",
  //     label: "Contact",
  //     minWidth: 170,
  //     sortable: true,
  // },
  {
    id: "createdAt",
    label: "Created Date",
    minWidth: 170,
    sortable: true,
  },
  {
    id: "action",
    label: "Action",
    minWidth: 100,
    align: "center" as const,
  },
];

const AdminUser = () => {
  const [showAdminBlade, setShowAdminBlade] = useState(false);
  const [leadershipCouncils, setLeadershipCouncils] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadershipCouncilToDelete, setLeadershipCouncilToDelete] = useState<
    string | null
  >(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [search, setSearch] = useState("");
  const searchRef = useRef<string>("");
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [uiSortField, setUiSortField] = useState<string>("createdAtFormatted");
  const [loading, setLoading] = useState<boolean>(false);
  const sortFieldMap: Record<string, string> = {
    createdAtFormatted: "createdAt",
    fullName: "fullName",
    email: "email",
  };
  const [userObject, setUserObject] = useState<AdminUserInterface | null>(null);
  const userInfo = JSON.parse(localStorage.getItem("UserData") || "{}");
  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });

  useEffect(() => {
    getAdminUserList(searchRef.current);
  }, [currentPage, sortField, sortOrder]);
  const getAdminUserList = (keyword: string = "") => {
    setLoading(true);
    const params = new URLSearchParams();
    params.append("page", currentPage.toString());
    params.append("limit", itemsPerPage.toString());

    // Keyword search
    if (keyword.trim()) {
      params.append("keyword", keyword.trim());
    }

    // Sorting
    params.append("sortField", sortField);
    params.append("sortOrder", sortOrder);

    WebService.getAPI({
      action: `admin-user/get-list?${params.toString()}`,
    })
      .then((res: any) => {
        const perm = res?.data?.permissions || {};

        setPagePermission({
          canView: perm.canView,
          canAdd: perm.canAdd,
          canEdit: perm.canEdit,
          canDelete: perm.canDelete,
        });
        const formattedRows = res?.data?.data.map((item: any) => {
          return {
            ...item,

            fullName: item.fullName || "-",
            username: item.username || "-",
            email: item.email || "-",
            role: item.role?.roleName || "-",
            createdAt: HelperService.getFormatedDate(item.createdAt),
            action: actionList(item, perm),
          };
        });
        setLeadershipCouncils(formattedRows);
        setTotalCount(res?.data?.pagination?.total);
      })
      .catch((err: any) => {
        console.error("Error fetching admin users:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeleteEvent = (leadershipCouncilId: string) => {
    setLeadershipCouncilToDelete(leadershipCouncilId);
    setShowDeleteModal(true);
  };

  const actionList = (item: any, perms:any) => {
    if (item._id === userInfo.id) {
      return null;
    }
    return (
      <div>
        {" "}
         {perms.canEdit && (
        <PencilFill
          className="me-3 cursor-pointer"
          onClick={() => {
            setShowAdminBlade(true);
            setUserObject(item);
          }}
          title="Edit"
        /> )}
          {perms.canDelete && (
        <TrashFill
          color="#DC143C"
          className="cursor-pointer"
          onClick={() => handleDeleteEvent(item._id)}
          title="Delete"
        /> )}
      </div>
    );
  };

  return (
    <>
      <AddAdminUser
        show={showAdminBlade}
        handleClose={() => {
          setShowAdminBlade(false);
          getAdminUserList(searchRef.current);
          setUserObject(null);
        }}
        data={userObject}
      />
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={() => {
          if (leadershipCouncilToDelete) {
            WebService.deleteAPI({
              action: `admin-user/delete-admin-user/${leadershipCouncilToDelete}`,
            }).then(() => {
              setShowDeleteModal(false);
              toast.success("Admin User deleted successfully.");
              getAdminUserList(searchRef.current);
            });
          }
        }}
        message="Are you sure you want to delete ?"
      />
      <div className="events-main">
        <Card className="event-card">
          <div className="d-flex justify-content-between">
            <div className="page-title">Admin Users</div>

            {pagePermission.canAdd && (
              <CustomButton
                isLoading={false}
                label="Add"
                onClick={() => {
                  setShowAdminBlade(true);
                  setUserObject(null);
                }}
                style={{ width: 100 }}
              />
            )}
          </div>
          <div className="row">
            <div className="col-md-3">
              <Form.Label className="label">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                value={search}
                onChange={(e) => {
                  const value = e.target.value;
                  searchRef.current = value;                  
                  setSearch(value);
                  setCurrentPage(1);
                  getAdminUserList(searchRef.current);
                }}
              />
            </div>
          </div>
          <CustomTableSortable
            columns={columns}
            rows={leadershipCouncils}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={itemsPerPage}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
            isLoading={loading}
            sortField={uiSortField}
            sortOrder={sortOrder}
            onSortChange={(columnId, order) => {
              setUiSortField(columnId);
              setSortOrder(order);
              const apiField = sortFieldMap[columnId] || "createdAt";
              setSortField(apiField);
              setCurrentPage(1);
              // Fetch is driven by the useEffect (now depends on sortField/sortOrder) using fresh state.
            }}
          />
        </Card>
      </div>
    </>
  );
};

export default AdminUser;
